"use client";
import { motion, Variants } from "framer-motion";
import {
  Container,
  CountUpValue,
  SectionSubHeading,
  SectionTitle,
} from "@/components";
import { TeamUniqueItem } from "@/lib/types";

interface TeamNumbersProps {
  items: TeamUniqueItem[];
}

const stat: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

// A "by the numbers" strip driven by `uniqueItems`. Each value counts up from
// zero as the section scrolls into view.
const TeamNumbers = ({ items }: TeamNumbersProps) => {
  return (
    <section className="bg-background py-14 md:py-20">
      <Container className="px-4 lg:px-10">
        <div className="space-y-3">
          <SectionSubHeading
            subtitle="By The Numbers"
            className="text-primary"
          />
          <SectionTitle title={<>NUMBERS DON&rsquo;T LIE.</>} />
        </div>

        <div className="mt-14 grid gap-10 sm:grid-cols-3 md:mt-20">
          {items.map((item, i) => (
            <motion.div
              key={item.label}
              variants={stat}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              className="relative"
            >
              <p className="font-black leading-none tracking-tighter text-6xl md:text-7xl xl:text-8xl">
                <CountUpValue value={item.value} />
              </p>
              <motion.span
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.12 + 0.35,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="mt-5 block h-1.5 w-16 origin-left rounded-full bg-primary"
              />
              <div className="mt-4 flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-primary" />
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground md:text-sm">
                  {item.label}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default TeamNumbers;
